import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { getEnvironment } from '../../utils/environment';
import { useAppContext } from '../../contexts/AppContext';
import { getCurrentTerms, getCurrentPrivacy, getCurrentLocation, LEGAL_VERSIONS } from '../../data/legal';
import { IoCheckmarkCircle, IoEllipseOutline, IoChevronForward } from 'react-icons/io5';
import { THEME } from '../../pages/spot/styles/SpotStyles';

const TosAgreementModal = ({ piUser, onComplete }) => {
    const { t } = useTranslation();
    const { savedAgreements, isAuthInitializing, authState } = useAppContext();

    const [showModal, setShowModal] = useState(false);
    const [agreements, setAgreements] = useState({ terms: false, privacy: false, location: false });
    const [detailView, setDetailView] = useState(null); // 'terms' | 'privacy' | 'location'
    const [saving, setSaving] = useState(false);

    // 약관 동의 필요 여부 체크
    useEffect(() => {
        if (isAuthInitializing) return;

        // 🎯 [방어 코드] authState가 undefined이거나 예상치 못한 값인 경우 처리
        if (!authState || (authState !== 'initializing' && authState !== 'authenticated' && authState !== 'guest')) {
            console.warn('[TosAgreementModal] Unexpected authState:', authState);
            return; // 안전하게 대기
        }

        // 🎯 [Fix] 로그인 대기 중 깜빡임 방지: authState가 'authenticated'인 경우 piUser 로드 대기
        // authState가 'authenticated'로 전환되었지만 piUser가 아직 null인 경우는 로그인 진행 중이므로 대기
        let timeoutId = null;
        if (authState === 'authenticated' && !piUser) {
            // 🎯 [방어 코드] 타임아웃 추가 (5초 후 경고 로그만 출력, 실제 대기는 계속)
            timeoutId = setTimeout(() => {
                console.warn('[TosAgreementModal] piUser timeout after 5s - authState:', authState, 'piUser:', piUser);
            }, 5000);
            return; // piUser가 로드될 때까지 대기
        }

        // [게스트] 세션에서 이미 동의했으면 스킵
        if (!piUser && sessionStorage.getItem('guest_tos_agreed')) {
            if (onComplete) onComplete();
            return;
        }

        // [게스트] 미동의 시 모달 표시
        // 🎯 [Fix] authState가 'guest'인 경우에만 게스트로 판단
        if (!piUser && authState === 'guest') {
            setShowModal(true);
            return;
        }

        // [로그인 유저] DB 로딩 대기
        if (savedAgreements === undefined) return;

        // [v2] 버전 비교
        const hasAgreed = savedAgreements?.termsVersion === LEGAL_VERSIONS.terms &&
            savedAgreements?.privacyVersion === LEGAL_VERSIONS.privacy &&
            savedAgreements?.locationVersion === LEGAL_VERSIONS.location;

        if (!hasAgreed) {
            setShowModal(true);
        } else {
            if (onComplete) onComplete();
        }
        
        // 🎯 [방어 코드] cleanup 함수: 타임아웃 정리
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [piUser, savedAgreements, onComplete, isAuthInitializing, authState]);

    const allChecked = agreements.terms && agreements.privacy && agreements.location;

    const toggleAll = () => {
        const newValue = !allChecked;
        setAgreements({ terms: newValue, privacy: newValue, location: newValue });
    };

    const toggleItem = (key) => {
        setAgreements(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleAgree = async () => {
        if (!allChecked || saving) return;
        setSaving(true);

        try {
            if (!piUser) {
                sessionStorage.setItem('guest_tos_agreed', 'true');
                window.dispatchEvent(new CustomEvent('guest-tos-agreed'));
                setShowModal(false);
                if (onComplete) onComplete();
                return;
            }

            const getUserCollection = () => {
                const env = getEnvironment();
                if (env === 'production') return 'users';
                if (env === 'testnet' || env === 'sandbox') return 'testnet_users';
                return 'dev_users';
            };

            await updateDoc(doc(db, getUserCollection(), piUser.uid), {
                agreements: {
                    termsVersion: LEGAL_VERSIONS.terms,
                    termsAgreedAt: serverTimestamp(),
                    privacyVersion: LEGAL_VERSIONS.privacy,
                    privacyAgreedAt: serverTimestamp(),
                    locationVersion: LEGAL_VERSIONS.location,
                    locationAgreedAt: serverTimestamp(),
                    language: getCurrentTerms()?.language || 'en'
                }
            });

            setShowModal(false);
            if (onComplete) onComplete();
        } catch (err) {
            console.error('[ToS] Failed to save:', err);
            alert(t('tos_save_error'));
        } finally {
            setSaving(false);
        }
    };

    if (!showModal) return null;

    // --- Render Helpers ---
    const RenderIcon = ({ checked, color = THEME.primary }) => (
        checked ?
            <IoCheckmarkCircle size={24} color={color} /> :
            <IoEllipseOutline size={24} color="#D1D5DB" />
    );

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            padding: '20px'
        }}>
            <div style={{
                background: 'white', width: '100%', maxWidth: '340px', borderRadius: '20px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                overflow: 'hidden', display: 'flex', flexDirection: 'column',
                position: 'relative', animation: 'fadeIn 0.3s ease-out'
            }}>
                <style>{`
                    @keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); }}
                `}</style>

                {/* Header */}
                <div style={{ padding: '24px 20px 16px 20px', textAlign: 'center' }}>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '1.15rem', fontWeight: '800', color: '#1F2937' }}>
                        {t('tos_title')}
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#6B7280', lineHeight: '1.4' }}>
                        {t('tos_desc')}
                    </p>
                </div>

                {/* Content */}
                <div style={{ padding: '0 16px 20px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>

                    {/* Agree All Box */}
                    <div
                        onClick={toggleAll}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '14px', background: '#F5F3FF', borderRadius: '12px',
                            cursor: 'pointer', marginBottom: '6px',
                            border: `1px solid ${allChecked ? THEME.primary : 'transparent'}`,
                            transition: 'all 0.2s'
                        }}
                    >
                        <RenderIcon checked={allChecked} />
                        <span style={{ flex: 1, fontSize: '0.95rem', fontWeight: '700', color: '#1F2937' }}>
                            {t('tos_agree_all_check')}
                        </span>
                    </div>

                    {/* Divider */}
                    <div style={{ height: '1px', background: '#F3F4F6', margin: '2px 0' }} />

                    {/* Individual Items */}
                    {['terms', 'privacy', 'location'].map((type) => (
                        <div key={type} style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '10px 4px', cursor: 'pointer',
                            borderBottom: '1px solid #F9FAFB'
                        }}>
                            <div onClick={() => toggleItem(type)} style={{ display: 'flex', alignItems: 'center' }}>
                                <RenderIcon checked={agreements[type]} />
                            </div>

                            <div onClick={() => toggleItem(type)} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '0.85rem', color: '#374151', fontWeight: '500' }}>
                                    {type === 'terms' && t('tos_terms')}
                                    {type === 'privacy' && t('tos_privacy')}
                                    {type === 'location' && t('tos_location')}
                                </span>
                                <span style={{ fontSize: '0.7rem', color: THEME.primary, fontWeight: '600', background: '#EEF2FF', padding: '1px 5px', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                                    {t('tos_required')}
                                </span>
                            </div>

                            <button
                                onClick={(e) => { e.stopPropagation(); setDetailView(type); }}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    padding: '6px', color: '#9CA3AF', display: 'flex', alignItems: 'center', flexShrink: 0
                                }}
                            >
                                <span style={{ fontSize: '0.75rem', marginRight: '1px' }}>{t('tos_view')}</span>
                                <IoChevronForward size={12} />
                            </button>
                        </div>
                    ))}
                </div>

                {/* Footer Button */}
                <div style={{ padding: '0 16px 16px 16px' }}>
                    <button
                        onClick={handleAgree}
                        disabled={!allChecked || saving}
                        style={{
                            width: '100%', padding: '14px', borderRadius: '12px', border: 'none',
                            background: allChecked ? THEME.primary : '#E5E7EB',
                            color: allChecked ? 'white' : '#9CA3AF',
                            fontSize: '0.95rem', fontWeight: '700', cursor: allChecked ? 'pointer' : 'not-allowed',
                            transition: 'all 0.2s', boxShadow: allChecked ? '0 4px 12px rgba(108, 92, 231, 0.3)' : 'none'
                        }}
                    >
                        {saving ? t('processing') : t('tos_agree_start')}
                    </button>
                </div>

                {/* Detail View Overlay */}
                {detailView && (
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'white', zIndex: 10, display: 'flex', flexDirection: 'column',
                        animtion: 'slideIn 0.2s ease-out'
                    }}>
                        <div style={{
                            padding: '14px', display: 'flex', alignItems: 'center',
                            borderBottom: '1px solid #E5E7EB'
                        }}>
                            <button onClick={() => setDetailView(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '8px', display: 'flex' }}>
                                <IoChevronForward size={20} style={{ transform: 'rotate(180deg)', color: '#1F2937' }} />
                            </button>
                            <span style={{ fontSize: '1rem', fontWeight: '700', color: '#1F2937' }}>
                                {detailView === 'terms' && t('tos_terms_title')}
                                {detailView === 'privacy' && t('tos_privacy_title')}
                                {detailView === 'location' && t('tos_location_title')}
                            </span>
                        </div>
                        <div style={{
                            flex: 1, overflowY: 'auto', padding: '16px', fontSize: '0.85rem',
                            color: '#4B5563', lineHeight: '1.6', whiteSpace: 'pre-wrap'
                        }}>
                            {detailView === 'terms' && getCurrentTerms()?.content}
                            {detailView === 'privacy' && getCurrentPrivacy()?.content}
                            {detailView === 'location' && getCurrentLocation()?.content}
                        </div>
                        <div style={{ padding: '16px', borderTop: '1px solid #E5E7EB' }}>
                            <button
                                onClick={() => {
                                    setAgreements(prev => ({ ...prev, [detailView]: true }));
                                    setDetailView(null);
                                }}
                                style={{
                                    width: '100%', padding: '14px', borderRadius: '12px', border: 'none',
                                    background: '#1F2937', color: 'white', fontWeight: '700', fontSize: '0.95rem',
                                    cursor: 'pointer'
                                }}
                            >
                                {t('tos_agree_confirm')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TosAgreementModal;
